import './FeaturesPageDraft2.css';

import React from 'react';

import { useNavigate } from 'react-router-dom';

import MarketingTopNav
  from '../components/common/MarketingTopNav/MarketingTopNav';
import { colors } from '../styles/colorPalette';

const MAROON = colors.primary.main;
const PINK = colors.brand.accentPink;
const GOLD = '#F2B544';

function Sparkle({ x = 0, y = 0, size = 14, color = GOLD }: { x?: number | string; y?: number | string; size?: number; color?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      style={{ position: 'absolute', left: x, top: y, pointerEvents: 'none' }}
      aria-hidden="true"
    >
      <path d="M12 0 L13.5 10.5 L24 12 L13.5 13.5 L12 24 L10.5 13.5 L0 12 L10.5 10.5 Z" fill={color} />
    </svg>
  );
}

function TapeStrip({ color = 'rgba(242,181,68,0.55)', rotate = '-8deg', top = -10, left = 30, right, width = 60 }: {
  color?: string; rotate?: string; top?: number; left?: number | string; right?: number | string; width?: number;
}) {
  return (
    <div
      style={{
        position: 'absolute',
        top,
        left,
        right,
        width,
        height: 18,
        background: color,
        transform: `rotate(${rotate})`,
        zIndex: 2,
        borderRadius: 2,
      }}
      aria-hidden="true"
    />
  );
}

function ScreenshotPlaceholder({ label, rotate = '0deg' }: { label: string; rotate?: string }) {
  return (
    <div className="fp2-screenshot-wrap" style={{ transform: `rotate(${rotate})` }}>
      <TapeStrip color="rgba(242,181,68,0.6)" rotate="-7deg" top={-10} left={28} width={58} />
      <TapeStrip color="rgba(255,107,157,0.5)" rotate="6deg" top={-8} right={28} width={48} />
      <div className="fp2-screenshot">
        <div className="fp2-screenshot-icon">📱</div>
        <div className="fp2-screenshot-label">{label}</div>
      </div>
    </div>
  );
}

const features = [
  {
    tag: '★ No accounts. No friction.',
    title: 'Coaches join in seconds.',
    body: 'Share a code. Scoring coaches open the link in any browser and they\'re in — no download, no sign-up. Admins manage; scorers just score.',
    screenshot: 'Join screen — code entry',
    tagColor: MAROON,
    rotate: '-1.5deg',
  },
  {
    tag: '★ Everything on one sheet.',
    title: 'Photos. Video. Skills. Comments.',
    body: 'Score each skill 1–5, snap a photo, record a clip, and leave notes — all on one athlete card. Every coach sees it live.',
    screenshot: 'Scoring screen — athlete card',
    tagColor: PINK,
    rotate: '1.5deg',
  },
  {
    tag: '★ Stay in the flow.',
    title: 'Jump between athletes instantly.',
    body: 'The quick-select bar keeps every athlete a single tap away. No back button, no losing your place mid-score.',
    screenshot: 'Quick-select tab bar',
    tagColor: MAROON,
    rotate: '-1deg',
  },
  {
    tag: '★ Your sport. Your criteria.',
    title: 'Custom forms, your way.',
    body: 'Name your skills, set the order, adjust the weights. Build the exact scoring form your program needs.',
    screenshot: 'Form builder screen',
    tagColor: PINK,
    rotate: '1.2deg',
  },
];

const steps = [
  { n: 1, t: 'Create your form', d: 'Set up skills and scoring criteria. Takes two minutes.', color: MAROON, rot: -1.5 },
  { n: 2, t: 'Add athletes', d: 'Name and a photo. No tryout numbers required.', color: PINK, rot: 1.5 },
  { n: 3, t: 'Score athletes', d: 'Rate, capture, comment — all live from the floor.', color: GOLD, rot: -1 },
  { n: 4, t: 'Rank & place', d: 'Auto-averages roll up. Drag athletes into teams.', color: MAROON, rot: 1 },
];

const FeaturesPageDraft2: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="fp2-container">
      <MarketingTopNav />

      {/* Hero */}
      <section className="fp2-hero">
        <Sparkle x={300} y={4} size={16} color={GOLD} />
        <Sparkle x={20} y={100} size={12} color={PINK} />
        <Sparkle x={330} y={140} size={10} color={MAROON} />

        <div className="fp2-hero-tag">
          ★ Tryouts, sorted
        </div>

        <h1 className="fp2-hero-title">
          Everything your{' '}
          <span className="fp2-hero-underline">
            tryout
            <svg className="fp2-squiggle" viewBox="0 0 120 10" preserveAspectRatio="none">
              <path d="M2 6 Q 30 2, 60 6 T 118 5" stroke={PINK} strokeWidth="3" fill="none" strokeLinecap="round" />
            </svg>
          </span>{' '}
          needs.
        </h1>

        <p className="fp2-hero-sub">
          One platform. Your whole coaching staff. Zero paper — from the first athlete to the final roster.
        </p>

        <div className="fp2-hero-buttons">
          <button className="fp2-btn-primary" onClick={() => navigate('/login')}>
            Get Started
          </button>
          <button className="fp2-btn-gold" onClick={() => navigate('/join')}>
            Join a Tryout ▸
          </button>
        </div>
      </section>

      {/* Features — sticker cards */}
      <section className="fp2-features">
        <h2 className="fp2-section-title">
          <em>What's inside</em>
        </h2>

        {features.map((f, i) => (
          <div key={i} className="fp2-feature-card" style={{ transform: `rotate(${f.rotate})` }}>
            <div className="fp2-feature-tag" style={{ background: f.tagColor }}>{f.tag}</div>
            <h3 className="fp2-feature-title">{f.title}</h3>
            <p className="fp2-feature-body">{f.body}</p>
            <ScreenshotPlaceholder label={f.screenshot} rotate="0deg" />
          </div>
        ))}
      </section>

      {/* Steps — sticker steps */}
      <section className="fp2-steps">
        <Sparkle x={20} y={20} size={14} color={GOLD} />
        <Sparkle x="90%" y={60} size={12} color={PINK} />

        <h2 className="fp2-steps-title">
          <em>How it works</em>
        </h2>

        {steps.map((s) => (
          <div
            key={s.n}
            className="fp2-step-card"
            style={{ transform: `rotate(${s.rot}deg)` }}
          >
            <div className="fp2-step-num" style={{ background: s.color }}>
              {s.n}
            </div>
            <div className="fp2-step-body">
              <h3 className="fp2-step-title">{s.t}</h3>
              <p className="fp2-step-desc">{s.d}</p>
            </div>
          </div>
        ))}
      </section>

      {/* CTA */}
      <section className="fp2-cta">
        <Sparkle x={30} y={30} size={16} color={GOLD} />
        <Sparkle x="85%" y={50} size={12} color={PINK} />

        <h2 className="fp2-cta-title">
          Ready to run your<br />
          <em style={{ color: PINK }}>best tryout?</em>
        </h2>
        <div className="fp2-cta-buttons">
          <button className="fp2-btn-primary" onClick={() => navigate('/login')}>
            Get Started
          </button>
          <button className="fp2-btn-gold" onClick={() => navigate('/join')}>
            Join a Tryout ▸
          </button>
        </div>
      </section>

      <footer className="fp2-footer">
        <div className="fp2-footer-tagline">
          ♡ Made by cheer coaches, for cheer coaches.
        </div>
        <p className="fp2-footer-links">
          © 2026 CheerEval ·{' '}
          <a href="/terms" className="fp2-footer-link">Terms</a>
          {' · '}
          <a href="/privacy" className="fp2-footer-link">Privacy</a>
          {' · '}
          <a href="/contact" className="fp2-footer-link">Contact</a>
        </p>
      </footer>
    </div>
  );
};

export default FeaturesPageDraft2;
